import { useEffect, useRef, useState } from 'react'

const CONFETTI_COLORS = ['#4A6D8C', '#6E92AE', '#AEBFCC', '#CDBB9A']
const CONFETTI_COUNT = 46
const CONFETTI_DURATION_MS = 1500

/**
 * DOM-based confetti burst, ported faithfully from docs/design/milestone-completion.html.
 * Chosen over a canvas-confetti dependency: the reference already specifies exact particle
 * count/palette/timing, and a handful of WAAPI-animated divs (transform/opacity only) is
 * lighter than a new dependency for a PWA/TWA target running on mid-range Android hardware.
 * Mounts once, animates for ~1.5s, then unmounts its own particle nodes.
 */
export function MilestoneConfetti({ active }: { active: boolean }) {
  const layerRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(active)

  useEffect(() => {
    if (!active) return
    const layer = layerRef.current
    if (!layer) return
    layer.innerHTML = ''
    const W = layer.clientWidth || 380
    const H = layer.clientHeight || 700
    for (let i = 0; i < CONFETTI_COUNT; i++) {
      const p = document.createElement('i')
      const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length]
      const round = Math.random() < 0.4
      const size = 6 + Math.random() * 7
      p.style.position = 'absolute'
      p.style.top = '0'
      p.style.left = '0'
      p.style.width = `${size}px`
      p.style.height = `${round ? size : size * 0.5}px`
      p.style.background = color
      p.style.borderRadius = round ? '50%' : '1px'
      p.style.opacity = '0'
      const x = 24 + Math.random() * Math.max(W - 48, 1)
      const rot = Math.random() * 360
      const endY = H * (0.5 + Math.random() * 0.42)
      const drift = Math.random() * 60 - 30
      p.animate(
        [
          { transform: `translate(${x}px, -30px) rotate(${rot}deg)`, opacity: 0, offset: 0 },
          { opacity: 1, offset: 0.12 },
          { opacity: 1, offset: 0.72 },
          { transform: `translate(${x + drift}px, ${endY}px) rotate(${rot + 280}deg)`, opacity: 0, offset: 1 },
        ],
        {
          duration: 1250 + Math.random() * 550,
          delay: Math.random() * 220,
          easing: 'cubic-bezier(.25,.7,.4,1)',
          fill: 'forwards',
        },
      )
      layer.appendChild(p)
    }
    const t = setTimeout(() => setVisible(false), CONFETTI_DURATION_MS)
    return () => clearTimeout(t)
  }, [active])

  if (!visible) return null
  return (
    <div
      ref={layerRef}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden', pointerEvents: 'none' }}
    />
  )
}

/** Vocabulary milestone numeral — counts up 0 → target once on mount, ~800ms, decelerating. */
export function CountUpNumeral({ target, reducedMotion }: { target: number; reducedMotion: boolean }) {
  const [value, setValue] = useState(reducedMotion ? target : 0)

  useEffect(() => {
    if (reducedMotion) {
      setValue(target)
      return
    }
    const duration = 800
    const start = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(Math.round(target * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      style={{
        fontSize: 72, fontWeight: 800, lineHeight: 0.9, letterSpacing: '-0.03em',
        color: 'var(--accent)', fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value.toLocaleString()}
    </div>
  )
}

/** Streak milestone numeral — single spring scale-in on mount, per DESIGN_SYSTEM.md. */
export function SpringNumeral({ value, reducedMotion }: { value: number; reducedMotion: boolean }) {
  return (
    <div
      style={{
        fontSize: 72, fontWeight: 800, lineHeight: 0.9, letterSpacing: '-0.03em',
        color: 'var(--accent)', fontVariantNumeric: 'tabular-nums',
        animation: reducedMotion ? undefined : 'milestoneSpringIn 780ms cubic-bezier(.34,1.56,.64,1) both',
      }}
    >
      {value.toLocaleString()}
    </div>
  )
}
